import { fetchWithAuth } from './api';
import { okJson } from './httpError';
import { normalizePhotoForUpload } from '../utils/uploadPhoto';

export interface MuralAnuncioPerson {
  id: string;
  first_name: string;
  last_name: string;
  identity_photo_url?: string | null;
  profile_picture_url?: string | null;
}

export interface MuralAnuncioComment {
  id: string;
  mural_anuncio_id: string;
  person_id: string;
  person?: MuralAnuncioPerson;
  content: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/** One person who curtiu — a name and a moment, nothing else. */
export interface MuralAnuncioLiker {
  person_id: string;
  person_name: string;
  created_at?: string;
}

export interface MuralAnuncio {
  id: string;
  customer_id: string;
  person_id?: string;
  person?: MuralAnuncioPerson;
  created_by_user_id?: string;
  created_by_name?: string;
  title: string;
  /** May be empty when the notice IS an attached PDF. */
  body: string;
  /**
   * An attached PDF, rendered INLINE as the notice itself (not offered as a download, which is
   * what a norma's attachment does): a mural PDF is usually a one-page notice, so the document is
   * the announcement.
   */
  pdf_url?: string | null;
  pdf_name?: string | null;
  pdf_size?: number | null;
  photo_url?: string;
  photo_url_2?: string;
  photo_url_3?: string;
  photo_url_4?: string;
  photo_url_5?: string;
  visible_to_role_types?: string[];
  /**
   * How many residents curtiram. The NAMES are staff-only and never ride on this payload — they
   * come from `listLikers`, which the backend gates to the síndico's side of staff.
   */
  likes_count?: number;
  /** Whether the CALLER curtiu it. Per-request by definition. */
  liked_by_me?: boolean;
  comments_enabled: boolean;
  commentable_role_types?: string[];
  /** false = deleted. Only reachable by staff asking for `include_deleted`. */
  is_active: boolean;
  /**
   * Taken off the board without being deleted: residents stop seeing it, staff keep seeing it
   * marked and can put it back. A different act from deleting, deliberately.
   */
  is_hidden?: boolean;
  hidden_at?: string | null;
  created_at: string;
  updated_at: string;
  comments?: MuralAnuncioComment[];
}

export interface MuralAnuncioPayload {
  title: string;
  /** Optional when `pdf_url` is set — the backend refuses a notice with neither. */
  body: string;
  pdf_url?: string | null;
  pdf_name?: string | null;
  pdf_size?: number | null;
  photo_url?: string | null;
  photo_url_2?: string | null;
  photo_url_3?: string | null;
  photo_url_4?: string | null;
  photo_url_5?: string | null;
  visible_to_role_types?: string[];
  comments_enabled?: boolean;
  commentable_role_types?: string[];
}

export interface UploadMuralAnuncioPhotoResponse {
  success: boolean;
  photo_url: string;
}

export interface UploadMuralAnuncioPdfResponse {
  success: boolean;
  pdf_url: string;
  /** Carried back so the notice can show the document's own name and warn about its size. */
  pdf_name: string;
  pdf_size: number;
}

/** Staff-only list scope. Residents asking for these get the ordinary feed regardless. */
export interface ListAnunciosOptions {
  includeHidden?: boolean;
  includeDeleted?: boolean;
}

export interface AddMuralAnuncioCommentPayload {
  content: string;
}

export const muralAnunciosService = {
  async listAnuncios(customerId: string, options: ListAnunciosOptions = {}): Promise<MuralAnuncio[]> {
    const query = new URLSearchParams();
    if (options.includeHidden) query.set('include_hidden', 'true');
    if (options.includeDeleted) query.set('include_deleted', 'true');
    const suffix = query.toString() ? `?${query}` : '';
    const res = await fetchWithAuth(`/api/v1/customers/${customerId}/mural-anuncios${suffix}`);
    if (!res.ok) throw new Error(`Failed to fetch mural anuncios: ${res.status}`);
    const data = await res.json();
    return data.data ?? [];
  },

  async getAnuncio(customerId: string, anuncioId: string): Promise<MuralAnuncio> {
    const res = await fetchWithAuth(`/api/v1/customers/${customerId}/mural-anuncios/${anuncioId}`);
    if (!res.ok) throw new Error(`Failed to fetch mural anuncio: ${res.status}`);
    const data = await res.json();
    return data.data;
  },

  async uploadPhoto(customerId: string, file: File): Promise<UploadMuralAnuncioPhotoResponse> {
    const formData = new FormData();
    formData.append('photo', await normalizePhotoForUpload(file));
    const res = await fetchWithAuth(`/api/v1/customers/${customerId}/mural-anuncios/photo`, {
      method: 'POST',
      body: formData,
    });
    const json = await okJson<any>(res, 'Erro ao enviar a foto');
    return json;
  },

  async uploadPdf(customerId: string, file: File): Promise<UploadMuralAnuncioPdfResponse> {
    const formData = new FormData();
    formData.append('pdf', file);
    const res = await fetchWithAuth(`/api/v1/customers/${customerId}/mural-anuncios/pdf`, {
      method: 'POST',
      body: formData,
    });
    // The backend's own message ("apenas arquivos PDF", "arquivo muito grande") is what the
    // uploader needs to read, so it is surfaced rather than replaced.
    return okJson<UploadMuralAnuncioPdfResponse>(res, 'Erro ao enviar o PDF');
  },

  async createAnuncio(customerId: string, payload: MuralAnuncioPayload): Promise<MuralAnuncio> {
    const res = await fetchWithAuth(`/api/v1/customers/${customerId}/mural-anuncios`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const json = await okJson<any>(res, 'Erro ao criar o anúncio');
    return json.data;
  },

  async updateAnuncio(customerId: string, anuncioId: string, payload: MuralAnuncioPayload): Promise<MuralAnuncio> {
    const res = await fetchWithAuth(`/api/v1/customers/${customerId}/mural-anuncios/${anuncioId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const json = await okJson<any>(res, 'Erro ao atualizar o anúncio');
    return json.data;
  },

  async deleteAnuncio(customerId: string, anuncioId: string): Promise<void> {
    const res = await fetchWithAuth(`/api/v1/customers/${customerId}/mural-anuncios/${anuncioId}`, {
      method: 'DELETE',
    });
    if (!res.ok) throw new Error(`Failed to delete mural anuncio: ${res.status}`);
  },

  /** Off the board for residents; still listed for staff, and reversible. */
  async setHidden(customerId: string, anuncioId: string, hidden: boolean): Promise<void> {
    const action = hidden ? 'hide' : 'unhide';
    const res = await fetchWithAuth(
      `/api/v1/customers/${customerId}/mural-anuncios/${anuncioId}/${action}`,
      { method: 'POST' }
    );
    await okJson(res, hidden ? 'Erro ao ocultar o anúncio' : 'Erro ao publicar o anúncio');
  },

  /**
   * WHO curtiu a notice — the síndico's reading of the room.
   *
   * Staff only; the backend answers 403 for a resident, and no resident-facing screen calls this.
   * On a board where the síndico posts "obras na piscina", the list of who approved is a political
   * fact about neighbours.
   */
  async listLikers(customerId: string, anuncioId: string): Promise<MuralAnuncioLiker[]> {
    const res = await fetchWithAuth(
      `/api/v1/customers/${customerId}/mural-anuncios/${anuncioId}/likes`
    );
    if (!res.ok) throw new Error('Erro ao carregar quem curtiu');
    const data = await res.json();
    return data.data ?? [];
  },

  async listComments(customerId: string, anuncioId: string): Promise<MuralAnuncioComment[]> {
    const res = await fetchWithAuth(`/api/v1/customers/${customerId}/mural-anuncios/${anuncioId}/comments`);
    if (!res.ok) throw new Error(`Failed to fetch comments: ${res.status}`);
    const data = await res.json();
    return data.data ?? [];
  },

  async addComment(customerId: string, anuncioId: string, payload: AddMuralAnuncioCommentPayload): Promise<MuralAnuncioComment> {
    const res = await fetchWithAuth(`/api/v1/customers/${customerId}/mural-anuncios/${anuncioId}/comments`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const json = await okJson<any>(res, 'Erro ao adicionar o comentário');
    return json.data;
  },

  async deleteComment(customerId: string, anuncioId: string, commentId: string): Promise<void> {
    const res = await fetchWithAuth(
      `/api/v1/customers/${customerId}/mural-anuncios/${anuncioId}/comments/${commentId}`,
      { method: 'DELETE' }
    );
    if (!res.ok) throw new Error(`Failed to delete comment: ${res.status}`);
  },
};
